import React, { useState } from 'react';
import { TruckIcon, StoreIcon, ClockIcon, MapPinIcon } from 'lucide-react';
import { MenuList } from '../components/order/MenuList';
import { CartPanel, Fulfilment } from '../components/order/CartPanel';
import { useCart } from '../hooks/useCart';
const slotsByFulfilment: Record<Fulfilment, string[]> = {
  camion: ['11h45', '12h00', '12h15', '12h30', '12h45', '13h00', '13h15'],
  labo: ['17h30', '18h00', '18h30', '19h00', '19h30']
};
const options: {
  id: Fulfilment;
  label: string;
  detail: string;
  icon: typeof TruckIcon;
}[] = [{
  id: 'camion',
  label: 'Click & collect au camion',
  detail: 'Place du Marché · aujourd’hui 11h30 – 14h00',
  icon: TruckIcon
}, {
  id: 'labo',
  label: 'Retrait au laboratoire',
  detail: '12 rue du Commerce · en soirée',
  icon: StoreIcon
}];
export function Order() {
  const {
    quantities,
    lines,
    count,
    total,
    add,
    remove,
    clear
  } = useCart();
  const [fulfilment, setFulfilment] = useState<Fulfilment>('camion');
  const [slot, setSlot] = useState(slotsByFulfilment.camion[1]);
  const [submitted, setSubmitted] = useState(false);
  const handleFulfilment = (next: Fulfilment) => {
    setFulfilment(next);
    setSlot(slotsByFulfilment[next][1] ?? slotsByFulfilment[next][0]);
    setSubmitted(false);
  };
  return <main className="paper-texture" data-id="element-356" data-mp="1m9bz3yten3lg">
      <section className="border-b-[3px] border-noir bg-canard py-12 md:py-16" data-id="element-357" data-mp="1madc48tze05t">
        <div className="mx-auto max-w-7xl px-4 md:px-8" data-id="element-358" data-mp="1q9vv2z28s160">
          <span className="inline-block -rotate-1 border-[3px] border-noir bg-jaune px-3 py-1 font-display text-xs uppercase tracking-[0.16em]" data-id="element-359" data-mp="1v9gutbp8kdjs">
            Click &amp; collect
          </span>
          <h1 className="mt-4 max-w-2xl font-display text-4xl uppercase leading-[0.9] text-creme md:text-6xl" data-id="element-360" data-mp="24zjvly2lmvso">
            Commander en ligne
          </h1>
          <p className="mt-4 max-w-xl text-lg text-creme/85" data-id="element-361" data-mp="2a07pcri7pqql">
            Choisissez vos tourtes, réservez votre créneau, passez récupérer sans faire la
            queue. Prêt en 15 minutes.
          </p>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 border-t border-creme/25 pt-6 text-creme" data-id="element-362" data-mp="evq45p0mku7m">
            <p className="flex items-center gap-2 text-sm font-semibold" data-id="element-363" data-mp="9y80oe5dn770">
              <MapPinIcon className="h-4 w-4 text-jaune" aria-hidden="true" data-id="element-364" data-mp="nolakm1twvyz" />
              Place du Marché, centre-ville
            </p>
            <p className="flex items-center gap-2 text-sm font-semibold" data-id="element-365" data-mp="iv8oaikmh19e">
              <ClockIcon className="h-4 w-4 text-jaune" aria-hidden="true" data-id="element-366" data-mp="xkeni6jgmrha" />
              Commandes jusqu’à 13h15
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16" data-id="element-367" data-mp="16lmk8k6bvk99">
        <fieldset data-id="element-368" data-mp="12lc3qdkwtbg5">
          <legend className="font-display text-xl uppercase" data-id="element-369" data-mp="12ksv5nh5fzes">1 · Comment récupérez-vous ?</legend>
          <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-370" data-mp="38d93fzjzaav6">
            {options.map(option => {
            const active = fulfilment === option.id;
            return <label key={option.id} className={`flex cursor-pointer items-start gap-3 border-[3px] border-noir px-5 py-4 transition-colors duration-150 ease-out ${active ? 'bg-jaune shadow-hard-sm' : 'bg-white hover:bg-jaune-light'}`} data-id="element-371" data-mp="2u8qx7t67rmre">
                  <input type="radio" name="fulfilment" value={option.id} checked={active} onChange={() => handleFulfilment(option.id)} className="mt-1 h-4 w-4 accent-rouge" data-id="element-372" data-mp="2pgha0kwhh0st" />
                  <span data-id="element-373" data-mp="21le8pdght4vg">
                    <span className="flex items-center gap-2 font-display text-base uppercase leading-none" data-id="element-374" data-mp="21m63im29myko">
                      <option.icon className="h-5 w-5" aria-hidden="true" data-id="element-375" data-mp="3vxulkx3snh9a" />
                      {option.label}
                    </span>
                    <span className="mt-1.5 block text-sm text-noir/70" data-id="element-376" data-mp="4qi5ymik7bvh">{option.detail}</span>
                  </span>
                </label>;
          })}
          </div>
        </fieldset>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.6fr_0.9fr]" data-id="element-377" data-mp="1vwml39i87t5y">
          <div data-id="element-378" data-mp="1qvyyr14y00i2">
            <h2 className="font-display text-xl uppercase" data-id="element-379" data-mp="1qvqir0diwf2n">2 · Composez votre commande</h2>
            <div className="mt-6" data-id="element-380" data-mp="1mvohgsbp9nnw">
              <MenuList quantities={quantities} onAdd={add} onRemove={remove} data-id="element-381" data-mp="1mudhl3ldl022" />
            </div>
          </div>

          <div data-id="element-382" data-mp="1hxwqc0ivq5kr">
            <h2 className="font-display text-xl uppercase lg:sr-only" data-id="element-383" data-mp="1d53mdev8j19q">3 · Validez</h2>
            <div className="mt-6 lg:mt-0" data-id="element-384" data-mp="1d30ne5c3txsv">
              <CartPanel lines={lines} count={count} total={total} fulfilment={fulfilment} slot={slot} slots={slotsByFulfilment[fulfilment]} submitted={submitted} onSlotChange={setSlot} onClear={() => {
              clear();
              setSubmitted(false);
            }} onSubmit={() => setSubmitted(true)} data-id="element-385" data-mp="1858w9djaafpq" />
            </div>
          </div>
        </div>
      </div>
    </main>;
}